import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  UserIcon,
  ShieldExclamationIcon,
  UserGroupIcon,
  IdentificationIcon,
  LinkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ToggleStatCard } from '@/components/ui/ToggleStatCard';
import { Button } from '@/components/ui/Button';
import { useDebounce } from '@/hooks/useDebounce';
import {
  subjectsService,
  Subject,
  SubjectStats,
  SubjectRoleType,
  SubjectRiskLevel,
} from '@/services/subjects.service';
import { getFullImageUrl } from '@/utils/imageUrl';

const LIST_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.03 } },
};

const STAT_CARD_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
};

const Subjects: React.FC = () => {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [stats, setStats] = useState<SubjectStats | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [roleFilter, setRoleFilter] = useState<SubjectRoleType | 'all'>('all');
  const [riskFilter, setRiskFilter] = useState<SubjectRiskLevel | 'all'>('all');
  const [highRiskOnly, setHighRiskOnly] = useState(false);
  const [addedLast30DaysOnly, setAddedLast30DaysOnly] = useState(false);

  const fetchSubjects = useCallback(async () => {
    try {
      setIsRefetching(true);
      setError(null);

      const [result, statsResult] = await Promise.all([
        subjectsService.getSubjects({
          search: debouncedSearchTerm || undefined,
          roleType: roleFilter,
          riskLevel: riskFilter,
          sortField: 'updatedAt',
          sortDirection: 'desc',
          page: 1,
          limit: 50,
        }),
        subjectsService.getSubjectStats().catch(() => null),
      ]);

      setSubjects(result.subjects);
      setStats(statsResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch subjects');
    } finally {
      setIsRefetching(false);
      setInitialLoading(false);
    }
  }, [debouncedSearchTerm, roleFilter, riskFilter]);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  // Stat card filters with no API equivalent (combined risk levels, created-in-window)
  // are applied client-side on the fetched list
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  const displayedSubjects = subjects.filter(
    (subject) =>
      (!highRiskOnly || subject.riskLevel === 'HIGH' || subject.riskLevel === 'CRITICAL') &&
      (!addedLast30DaysOnly || Date.now() - new Date(subject.createdAt).getTime() <= THIRTY_DAYS_MS)
  );

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Subject Registry
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400">
            Centralized directory of people linked to investigations and incidents
          </p>
        </div>
        <Button
          className="flex items-center space-x-2"
          onClick={() => navigate('/security/subjects/create')}
        >
          <PlusIcon className="w-4 h-4" />
          <span>New Subject</span>
        </Button>
      </div>

      {stats && (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={LIST_VARIANTS}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={STAT_CARD_VARIANTS} className="flex">
            <ToggleStatCard
              label="Total Subjects"
              className="flex-1"
              active={roleFilter === 'all' && riskFilter === 'all' && !highRiskOnly && !addedLast30DaysOnly}
              onToggle={() => {
                setRoleFilter('all');
                setRiskFilter('all');
                setHighRiskOnly(false);
                setAddedLast30DaysOnly(false);
              }}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      Total Subjects
                    </p>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                      {stats.total}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                    <UserGroupIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
              </CardContent>
            </ToggleStatCard>
          </motion.div>

          <motion.div variants={STAT_CARD_VARIANTS} className="flex">
            <ToggleStatCard
              label="Suspects"
              className="flex-1"
              active={roleFilter === 'SUSPECT'}
              activeRingClass="ring-danger-500"
              onToggle={() => setRoleFilter(roleFilter === 'SUSPECT' ? 'all' : 'SUSPECT')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      Suspects
                    </p>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                      {stats.byRole?.SUSPECT ?? 0}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-danger-100 dark:bg-danger-900/20 rounded-lg flex items-center justify-center">
                    <ShieldExclamationIcon className="w-4 h-4 text-danger-600 dark:text-danger-400" />
                  </div>
                </div>
              </CardContent>
            </ToggleStatCard>
          </motion.div>

          <motion.div variants={STAT_CARD_VARIANTS} className="flex">
            <ToggleStatCard
              label="High/Critical Risk"
              className="flex-1"
              active={highRiskOnly}
              activeRingClass="ring-warning-500"
              onToggle={() => {
                setHighRiskOnly(!highRiskOnly);
                setRiskFilter('all');
              }}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      High/Critical Risk
                    </p>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                      {(stats.byRisk?.HIGH ?? 0) + (stats.byRisk?.CRITICAL ?? 0)}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-warning-100 dark:bg-warning-900/20 rounded-lg flex items-center justify-center">
                    <ExclamationTriangleIcon className="w-4 h-4 text-warning-600 dark:text-warning-400" />
                  </div>
                </div>
              </CardContent>
            </ToggleStatCard>
          </motion.div>

          <motion.div variants={STAT_CARD_VARIANTS} className="flex">
            <ToggleStatCard
              label="Added (30d)"
              className="flex-1"
              active={addedLast30DaysOnly}
              activeRingClass="ring-secondary-500"
              onToggle={() => setAddedLast30DaysOnly(!addedLast30DaysOnly)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      Added (30d)
                    </p>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                      {stats.createdInLast30Days ?? 0}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-secondary-100 dark:bg-secondary-700 rounded-lg flex items-center justify-center">
                    <LinkIcon className="w-4 h-4 text-secondary-600 dark:text-secondary-300" />
                  </div>
                </div>
              </CardContent>
            </ToggleStatCard>
          </motion.div>
        </motion.div>
      )}

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, phone, case..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-9 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                />
                {isRefetching && (
                  <div
                    aria-label="Searching"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"
                  />
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as SubjectRoleType | 'all')}
                className="pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
              >
                <option value="all">All Roles</option>
                <option value="SUSPECT">Suspect</option>
                <option value="VICTIM">Victim</option>
                <option value="WITNESS">Witness</option>
                <option value="LAW_ENFORCEMENT">Law Enforcement</option>
                <option value="REPORTING_PARTY">Reporting Party</option>
                <option value="OTHER">Other</option>
              </select>

              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value as SubjectRiskLevel | 'all')}
                className="pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
              >
                <option value="all">All Risk Levels</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Subjects</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-8">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mx-auto mb-4" />
              <p className="text-danger-600 dark:text-danger-400">{error}</p>
              <Button variant="outline" className="mt-4" onClick={fetchSubjects}>
                Try Again
              </Button>
            </div>
          ) : displayedSubjects.length === 0 ? (
            <div className="text-center py-8">
              <UserIcon className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
              <p className="text-secondary-600 dark:text-secondary-400">No subjects found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedSubjects.map((subject) => {
                const roleConfig = subjectsService.getRoleTypeConfig(subject.roleType);
                const riskConfig = subjectsService.getRiskLevelConfig(subject.riskLevel);
                const displayName = subjectsService.getSubjectDisplayName(subject);

                return (
                  <div
                    key={subject.id}
                    className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer bg-white dark:bg-secondary-800"
                    onClick={() => navigate(`/security/subjects/${subject.id}`)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center">
                        {getFullImageUrl(subject.photoUrl) ? (
                          <img
                            src={getFullImageUrl(subject.photoUrl) ?? undefined}
                            alt={displayName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <IdentificationIcon className="w-6 h-6 text-secondary-400" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-secondary-900 dark:text-white truncate">
                          {displayName}
                        </h3>
                        {subject.email && (
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                            {subject.email}
                          </p>
                        )}
                        {subject.phone && (
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                            {subject.phone}
                          </p>
                        )}

                        <div className="mt-2 flex flex-wrap gap-1">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${subjectsService.getBadgeClasses(roleConfig.color)}`}
                          >
                            {roleConfig.label}
                          </span>
                          {subject.riskLevel && (
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded-full ${subjectsService.getBadgeClasses(riskConfig.color)}`}
                            >
                              {riskConfig.label} risk
                            </span>
                          )}
                        </div>

                        <div className="mt-2 flex items-center gap-3 text-xs text-secondary-500 dark:text-secondary-400">
                          <span className="flex items-center gap-1">
                            <LinkIcon className="w-3 h-3" />
                            {(subject.linkedInvestigationsCount ?? 0) +
                              (subject.linkedIncidentsCount ?? 0)}{' '}
                            cases
                          </span>
                          <span>Updated {formatDate(subject.updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Subjects;
